/**
 * Внешнее хранилище состояния для `useSyncExternalStore`.
 *
 * PWA-службы (установка, обновление service worker, состояние сети) живут вне
 * React, а интерфейс подписывается на них через `subscribe`/`getSnapshot`.
 * Базовый класс держит неизменяемый снимок и список подписчиков; наследник
 * меняет состояние только через `update`, после чего все подписчики
 * оповещаются. Снимок при каждом изменении — новый объект, а между
 * изменениями `getSnapshot` возвращает один и тот же, как того требует React.
 *
 * Общая основа `InstallController`, `ServiceWorkerUpdater` и `ConnectionMonitor`:
 * раньше каждый из них дословно повторял эту механику.
 */
export abstract class SnapshotStore<T extends object> {
  #snapshot: T;
  readonly #listeners = new Set<() => void>();

  protected constructor(initial: T) {
    this.#snapshot = initial;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);

    return () => this.#listeners.delete(listener);
  };

  readonly getSnapshot = (): T => this.#snapshot;

  /** Текущий снимок для чтения внутри наследника. */
  protected get current(): T {
    return this.#snapshot;
  }

  /** Обновляет часть снимка и оповещает подписчиков. */
  protected update(patch: Partial<T>): void {
    this.#snapshot = { ...this.#snapshot, ...patch };
    this.notify();
  }

  /** Оповещает подписчиков, не меняя снимка. */
  protected notify(): void {
    for (const listener of this.#listeners) listener();
  }
}
